import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Rocket, 
  Plus, 
  Search, 
  RefreshCw, 
  Trash2, 
  Eye,
  Activity,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  RotateCcw,
  Play,
  Pause,
  Settings
} from 'lucide-react';
import { apiClient } from '@/services/api-client';

// Types
interface Deployment {
  id: string;
  service: string;
  version: string;
  status: 'successful' | 'failed' | 'in_progress' | 'pending';
  timestamp: string;
  environment: string;
  logs?: string[];
  metrics?: {
    build_time: string;
    deployment_time: string;
    downtime: string;
  };
}

interface DeploymentStats {
  total: number;
  successful: number;
  failed: number;
  in_progress: number;
}

// Deployment Manager Component
export default function DeploymentManager() {
  // State management
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentStats, setDeploymentStats] = useState<DeploymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeployDialogOpen, setIsDeployDialogOpen] = useState(false);

  // Form state for create/deploy
  const [formData, setFormData] = useState({
    service: '',
    version: '',
    environment: 'production'
  });

  // Load initial data
  useEffect(() => {
    loadDeploymentData();
  }, []);

  // Load deployment data
  const loadDeploymentData = async () => {
    try {
      setLoading(true);
      
      const response = await apiClient.get('/api/v1/deployments/') as any;
      
      if (response.success) {
        const data = response.data;
        setDeployments(data.deployments || []);
        setDeploymentStats({
          total: data.total || 0,
          successful: data.successful || 0,
          failed: data.failed || 0,
          in_progress: data.in_progress || 0
        });
      }
    } catch (error) {
      console.error('Failed to load deployment data:', error);
      alert('Failed to load deployment data');
    } finally {
      setLoading(false);
    }
  };

  // Create deployment
  const handleCreateDeployment = async () => {
    try {
      const response = await apiClient.post('/api/v1/deployments/', {
        service: formData.service,
        version: formData.version,
        environment: formData.environment
      }) as any;

      if (response.success) {
        alert('Deployment created successfully');
        setIsCreateDialogOpen(false);
        setFormData({ service: '', version: '', environment: 'production' });
        loadDeploymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to create deployment:', error);
      alert('Failed to create deployment');
    }
  };

  // Deploy service
  const handleDeployService = async () => {
    try {
      const response = await apiClient.post('/api/v1/deployments/deploy') as any;

      if (response.success) {
        alert('Deployment started successfully');
        setIsDeployDialogOpen(false);
        loadDeploymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to start deployment:', error);
      alert('Failed to start deployment');
    }
  };

  // Rollback deployment
  const handleRollbackDeployment = async (deploymentId: string) => {
    if (!confirm('Are you sure you want to rollback this deployment?')) return;

    try {
      const response = await apiClient.post(`/api/v1/deployments/${deploymentId}/rollback`) as any;
      
      if (response.success) {
        alert('Deployment rollback initiated successfully');
        loadDeploymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to rollback deployment:', error);
      alert('Failed to rollback deployment');
    }
  };

  // Delete deployment
  const handleDeleteDeployment = async (deploymentId: string) => {
    if (!confirm('Are you sure you want to delete this deployment?')) return;

    try {
      const response = await apiClient.delete(`/api/v1/deployments/${deploymentId}`) as any;
      
      if (response.success) {
        alert('Deployment deleted successfully');
        loadDeploymentData(); // Refresh data
      }
    } catch (error) {
      console.error('Failed to delete deployment:', error);
      alert('Failed to delete deployment');
    }
  };

  // Filtered deployments
  const filteredDeployments = deployments.filter(deployment => {
    return deployment.service.toLowerCase().includes(searchTerm.toLowerCase()) ||
           deployment.version.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'successful':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Successful</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500"><Activity className="w-3 h-3 mr-1" />In Progress</Badge>;
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />Unknown</Badge>;
    }
  };

  // Get environment badge
  const getEnvironmentBadge = (environment: string) => {
    switch (environment) {
      case 'production':
        return <Badge className="bg-red-500">Production</Badge>;
      case 'staging':
        return <Badge className="bg-yellow-500">Staging</Badge>;
      case 'development':
        return <Badge className="bg-blue-500">Development</Badge>;
      default:
        return <Badge variant="outline">{environment}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading deployment data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Deployment Manager</h1>
          <p className="text-muted-foreground">
            Manage deployments, monitor status, and control service rollouts
          </p>
        </div>
        <Button
          aria-label="Refresh Deployments"
          onClick={loadDeploymentData}
          variant="outline"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Deployment Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {deploymentStats && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Deployments</CardTitle>
                <Rocket className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{deploymentStats.total}</div>
                <p className="text-xs text-muted-foreground">
                  All time deployments
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Successful</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-500">{deploymentStats.successful}</div>
                <p className="text-xs text-muted-foreground">
                  {deploymentStats.total > 0 ? Math.round((deploymentStats.successful / deploymentStats.total) * 100) : 0}% success rate
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Failed</CardTitle>
                <XCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-500">{deploymentStats.failed}</div>
                <p className="text-xs text-muted-foreground">
                  Failed deployments
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">In Progress</CardTitle>
                <Activity className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-500">{deploymentStats.in_progress}</div>
                <p className="text-xs text-muted-foreground">
                  Active deployments
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="deployments" className="space-y-4">
        <TabsList>
          <TabsTrigger value="deployments">Deployments ({deploymentStats?.total || 0})</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="operations">Operations</TabsTrigger>
        </TabsList>

        {/* Deployments Tab */}
        <TabsContent value="deployments" className="space-y-4">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  aria-label="Search Deployments"
                  placeholder="Search deployments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Deployment
            </Button>
            <Button onClick={() => setIsDeployDialogOpen(true)} variant="outline">
              <Play className="w-4 h-4 mr-2" />
              Deploy Service
            </Button>
          </div>

          {/* Deployments Table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Deployments</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Environment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDeployments.map((deployment) => (
                    <TableRow key={deployment.id}>
                      <TableCell className="font-mono text-sm">{deployment.id}</TableCell>
                      <TableCell className="font-medium">{deployment.service}</TableCell>
                      <TableCell className="font-mono text-sm">{deployment.version}</TableCell>
                      <TableCell>
                        {getEnvironmentBadge(deployment.environment)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(deployment.status)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(deployment.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                          {deployment.status === 'successful' && (
                            <Button
                              variant="outline"
                              size="sm"
                              aria-label="Rollback Deployment"
                              onClick={() => handleRollbackDeployment(deployment.id)}
                            >
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label="Delete Deployment"
                            onClick={() => handleDeleteDeployment(deployment.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Deployment History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {deployments.slice(0, 10).map((deployment) => (
                  <div key={deployment.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {getStatusBadge(deployment.status)}
                      </div>
                      <div>
                        <h4 className="font-medium">{deployment.service}</h4>
                        <p className="text-sm text-muted-foreground">
                          Version {deployment.version} • {deployment.environment}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{deployment.id}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(deployment.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Rocket className="w-5 h-5 mr-2" />
                  Deployment Operations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button className="w-full" variant="outline">
                  <Play className="w-4 h-4 mr-2" />
                  Deploy to Production
                </Button>
                <Button className="w-full" variant="outline">
                  <Pause className="w-4 h-4 mr-2" />
                  Pause Deployments
                </Button>
                <Button className="w-full" variant="outline">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Rollback All
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button className="w-full" variant="outline">
                  <Settings className="w-4 h-4 mr-2" />
                  Deployment Settings
                </Button>
                <Button className="w-full" variant="outline">
                  <Activity className="w-4 h-4 mr-2" />
                  Health Checks
                </Button>
                <Button className="w-full" variant="outline">
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule Deployments
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Deployment Dialog */}
      {isCreateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create New Deployment</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Service</label>
                <Input
                  aria-label="Service Name"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  placeholder="Enter service name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Version</label>
                <Input
                  aria-label="Version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="Enter version (e.g., 1.0.0)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Environment</label>
                <select
                  aria-label="Environment"
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateDeployment}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* Deploy Service Dialog */}
      {isDeployDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Deploy Service</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Service</label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">Select a service</option>
                  <option value="ai-service">AI Service</option>
                  <option value="rag-service">RAG Service</option>
                  <option value="smart-ops">Smart Ops</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Version</label>
                <Input
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  placeholder="Enter version (e.g., 1.0.0)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Environment</label>
                <select
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="development">Development</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setIsDeployDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleDeployService}>Deploy</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
